import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";

// Soft radial alpha puff used for camera-facing volumetric smoke billboards.
function smokeTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const grd = g.createRadialGradient(64, 64, 4, 64, 64, 64);
  grd.addColorStop(0, "rgba(255,255,255,0.92)");
  grd.addColorStop(0.5, "rgba(255,255,255,0.34)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

// Lighting-reactive volumetric smoke: camera-facing MeshStandardMaterial planes
// that drift + rise, tinted by nearby lights (flashlight, fire) for real depth.
// Kept lean (few large puffs) so it holds 90+ FPS in VR.
export default function VolumetricSmoke({
  count = 16,
  area = [6, 4, 3],
  center = [0, 1.8, -1.5],
  color = 0x2b2a27,
  opacity = 0.5,
  rise = 0.28,
  getIntensity,
  clearPoint,
  clearRadius = 1.6,
}) {
  const tex = useMemo(smokeTexture, []);
  const grp = useRef();
  const camera = useThree((s) => s.camera);
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: tex,
        color,
        transparent: true,
        opacity,
        depthWrite: false,
        roughness: 1,
        metalness: 0,
      }),
    [tex, color, opacity]
  );
  const puffs = useMemo(() => {
    const a = [];
    for (let i = 0; i < count; i++) {
      a.push({
        x: center[0] + (Math.random() - 0.5) * area[0],
        y: center[1] + (Math.random() - 0.5) * area[1],
        z: center[2] + (Math.random() - 0.5) * area[2],
        s: 1.8 + Math.random() * 2.6,
        ph: Math.random() * Math.PI * 2,
        sp: 0.4 + Math.random() * 0.7,
      });
    }
    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useFrame((state, dt) => {
    if (!grp.current) return;
    const intensity = getIntensity ? getIntensity() : 1;
    mat.opacity = opacity * intensity;
    grp.current.visible = intensity > 0.02;
    const t = state.clock.elapsedTime;
    const cr2 = clearRadius * clearRadius;
    grp.current.children.forEach((c, i) => {
      const p = puffs[i];
      p.y += dt * rise * p.sp;
      if (p.y > center[1] + area[1]) p.y = center[1] - area[1];
      const px = p.x + Math.sin(t * 0.3 + p.ph) * 0.5;
      const py = p.y;
      const pz = p.z + Math.cos(t * 0.24 + p.ph) * 0.5;
      c.position.set(px, py, pz);
      let s = p.s * (0.9 + 0.1 * Math.sin(t * 0.5 + p.ph));
      // proximity transparency: shrink smoke that overlaps the survivor so they're never fully obscured
      if (clearPoint) {
        const dx = px - clearPoint[0], dy = py - clearPoint[1], dz = pz - clearPoint[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < cr2) s *= Math.max(0.12, Math.sqrt(d2) / clearRadius);
      }
      c.scale.setScalar(s);
      c.quaternion.copy(camera.quaternion);
    });
  });

  return (
    <group ref={grp}>
      {puffs.map((p, i) => (
        <mesh key={i} material={mat}>
          <planeGeometry args={[1, 1]} />
        </mesh>
      ))}
    </group>
  );
}
